// client/src/components/pages/Geofencing/zoneEditor/zoneGeometry.ts
// Pure geometry for the zone editor (ST-00239). No React, no map: everything the
// editor measures, checks or reshapes lives here so it can be unit-tested on its
// own. Points are GeoJSON order [lng, lat]; polygon rings are OPEN (the closing
// vertex is never repeated). The stored geofence format ([lat, lng] pairs) is
// converted at the edges, in zoneFormat.ts.
//
// Accuracy: areas use the spherical-excess formula (as turf's ringArea), lengths
// the haversine distance; topology checks (self-intersection, overlap) run on a
// local metric projection, which is exact enough for zones up to tens of km.

export type LngLat = [number, number];

export type ZoneShape =
  { kind: 'polygon'; ring: LngLat[] } | { kind: 'circle'; center: LngLat; radiusM: number };

/** Mean earth radius (m) for distances; WGS-84 equatorial radius for areas (as turf). */
export const EARTH_RADIUS_M = 6371008.8;
const AREA_RADIUS_M = 6378137;

export const MIN_VERTICES = 3;
/** The server refuses more (geofenceGeometry.ts); imports above it are simplified first. */
export const MAX_VERTICES = 2000;
export const MIN_RADIUS_M = 1;
export const MAX_RADIUS_M = 50000;
/** Below this radius / side a zone is smaller than typical GPS scatter. */
export const GPS_ACCURACY_M = 15;
/** Smallest polygon the editor accepts (m²) — anything smaller is a slip of the mouse. */
export const MIN_AREA_M2 = 1;

const toRad = (d: number): number => (d * Math.PI) / 180;
const toDeg = (r: number): number => (r * 180) / Math.PI;

/** Index into a ring with wrap-around (never undefined for a non-empty ring). */
export function vertexAt(ring: readonly LngLat[], i: number): LngLat {
  const n = ring.length;
  return ring[((i % n) + n) % n]!;
}

export function samePoint(a: LngLat, b: LngLat, epsDeg = 1e-12): boolean {
  return Math.abs(a[0] - b[0]) <= epsDeg && Math.abs(a[1] - b[1]) <= epsDeg;
}

export function isFiniteLngLat(p: unknown): p is LngLat {
  return (
    Array.isArray(p) &&
    p.length >= 2 &&
    typeof p[0] === 'number' &&
    typeof p[1] === 'number' &&
    Number.isFinite(p[0]) &&
    Number.isFinite(p[1]) &&
    p[0] >= -180 &&
    p[0] <= 180 &&
    p[1] >= -90 &&
    p[1] <= 90
  );
}

// ---------- distances, areas ----------

/** Great-circle distance in metres. */
export function haversineM(a: LngLat, b: LngLat): number {
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** Geodesic area of an open ring in m² (spherical excess; orientation-free). */
export function ringAreaM2(ring: readonly LngLat[]): number {
  const n = ring.length;
  if (n < 3) return 0;
  let total = 0;
  for (let i = 0; i < n; i++) {
    const lower = vertexAt(ring, i);
    const middle = vertexAt(ring, i + 1);
    const upper = vertexAt(ring, i + 2);
    total += (toRad(upper[0]) - toRad(lower[0])) * Math.sin(toRad(middle[1]));
  }
  return Math.abs((total * AREA_RADIUS_M * AREA_RADIUS_M) / 2);
}

/** Closed-loop perimeter of an open ring in metres. */
export function ringPerimeterM(ring: readonly LngLat[]): number {
  const n = ring.length;
  if (n < 2) return 0;
  let sum = 0;
  for (let i = 0; i < n; i++) sum += haversineM(vertexAt(ring, i), vertexAt(ring, i + 1));
  return n === 2 ? sum / 2 : sum;
}

export function shapeAreaM2(shape: ZoneShape): number {
  return shape.kind === 'circle' ? Math.PI * shape.radiusM ** 2 : ringAreaM2(shape.ring);
}

export function shapePerimeterM(shape: ZoneShape): number {
  return shape.kind === 'circle' ? 2 * Math.PI * shape.radiusM : ringPerimeterM(shape.ring);
}

const fmt = (v: number, digits: number): string =>
  v.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits });

/** "850 m²" below a hectare, else "12.4 ha" (2 decimals under 10 ha, 0 from 1,000 ha). */
export function formatArea(m2: number): string {
  if (!Number.isFinite(m2) || m2 <= 0) return '0 m²';
  if (m2 < 10000) return `${fmt(Math.round(m2), 0)} m²`;
  const ha = m2 / 10000;
  if (ha < 10) return `${fmt(ha, 2)} ha`;
  if (ha < 1000) return `${fmt(ha, 1)} ha`;
  return `${fmt(ha, 0)} ha`;
}

/** "845 m" below a kilometre, else "2.35 km". */
export function formatLength(m: number): string {
  if (!Number.isFinite(m) || m <= 0) return '0 m';
  if (m < 1000) return `${fmt(m < 10 ? Math.round(m * 10) / 10 : Math.round(m), m < 10 ? 1 : 0)} m`;
  return `${fmt(m / 1000, m < 10000 ? 2 : 1)} km`;
}

// ---------- local metric projection ----------

type XY = [number, number];

/** Equirectangular projection to metres around `origin` (good to well under 0.1% within 50 km). */
function projector(origin: LngLat): { to: (p: LngLat) => XY; from: (q: XY) => LngLat } {
  const k = (Math.PI / 180) * EARTH_RADIUS_M;
  const cos = Math.cos(toRad(origin[1])) || 1e-9;
  return {
    to: (p) => [(p[0] - origin[0]) * k * cos, (p[1] - origin[1]) * k],
    from: (q) => [origin[0] + q[0] / (k * cos), origin[1] + q[1] / k],
  };
}

const cross = (o: XY, a: XY, b: XY): number =>
  (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

/**
 * Where segment p1-p2 meets segment p3-p4 (planar), or null. Collinear overlaps
 * report the first shared point. `eps` is in the plane's units (metres).
 */
function segmentIntersection(p1: XY, p2: XY, p3: XY, p4: XY, eps: number): XY | null {
  const d1 = cross(p3, p4, p1);
  const d2 = cross(p3, p4, p2);
  const d3 = cross(p1, p2, p3);
  const d4 = cross(p1, p2, p4);
  const len12 = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]) || 1;
  const len34 = Math.hypot(p4[0] - p3[0], p4[1] - p3[1]) || 1;
  const tol12 = eps * len12;
  const tol34 = eps * len34;
  if (
    ((d1 > tol34 && d2 < -tol34) || (d1 < -tol34 && d2 > tol34)) &&
    ((d3 > tol12 && d4 < -tol12) || (d3 < -tol12 && d4 > tol12))
  ) {
    const t = d1 / (d1 - d2);
    return [p1[0] + t * (p2[0] - p1[0]), p1[1] + t * (p2[1] - p1[1])];
  }
  const onSeg = (a: XY, b: XY, p: XY): boolean =>
    Math.min(a[0], b[0]) - eps <= p[0] &&
    p[0] <= Math.max(a[0], b[0]) + eps &&
    Math.min(a[1], b[1]) - eps <= p[1] &&
    p[1] <= Math.max(a[1], b[1]) + eps;
  if (Math.abs(d1) <= tol34 && onSeg(p3, p4, p1)) return p1;
  if (Math.abs(d2) <= tol34 && onSeg(p3, p4, p2)) return p2;
  if (Math.abs(d3) <= tol12 && onSeg(p1, p2, p3)) return p3;
  if (Math.abs(d4) <= tol12 && onSeg(p1, p2, p4)) return p4;
  return null;
}

export interface SelfIntersection {
  /** Where the outline crosses itself. */
  point: LngLat;
  /** The two crossing edges (edge i runs from vertex i to vertex i + 1). */
  edges: [number, number];
}

/**
 * Every place an open ring's outline touches or crosses itself (non-adjacent
 * edges meeting, or adjacent edges folding back over each other). Sweep over
 * edge bounding boxes, so a 2,000-corner ring is checked in milliseconds.
 */
export function findSelfIntersections(ring: readonly LngLat[], limit = 50): SelfIntersection[] {
  const n = ring.length;
  if (n < 4) {
    // A triangle cannot cross itself; it can only be flat (zero area), checked separately.
    return [];
  }
  const proj = projector(ring[0]!);
  const pts = ring.map(proj.to);
  const eps = 1e-6; // metres
  type Edge = { i: number; a: XY; b: XY; minX: number; maxX: number; minY: number; maxY: number };
  const edges: Edge[] = [];
  for (let i = 0; i < n; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % n]!;
    edges.push({
      i,
      a,
      b,
      minX: Math.min(a[0], b[0]),
      maxX: Math.max(a[0], b[0]),
      minY: Math.min(a[1], b[1]),
      maxY: Math.max(a[1], b[1]),
    });
  }
  const out: SelfIntersection[] = [];
  const seen = new Set<string>();
  const push = (e1: number, e2: number, q: XY): void => {
    const key = `${Math.min(e1, e2)}:${Math.max(e1, e2)}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ point: proj.from(q), edges: [Math.min(e1, e2), Math.max(e1, e2)] });
  };

  // Adjacent edges only meet at their shared vertex — unless they fold back
  // over each other (a spike), which leaves a zero-width sliver.
  for (let i = 0; i < n; i++) {
    const e1 = edges[i]!;
    const e2 = edges[(i + 1) % n]!;
    const shared = e1.b;
    const u: XY = [e1.a[0] - shared[0], e1.a[1] - shared[1]];
    const v: XY = [e2.b[0] - shared[0], e2.b[1] - shared[1]];
    const lu = Math.hypot(u[0], u[1]);
    const lv = Math.hypot(v[0], v[1]);
    if (lu < eps || lv < eps) continue; // duplicate corner: dedupeRing's job
    const sin = (u[0] * v[1] - u[1] * v[0]) / (lu * lv);
    const cos = (u[0] * v[0] + u[1] * v[1]) / (lu * lv);
    if (Math.abs(sin) < 1e-9 && cos > 0) push(e1.i, e2.i, shared);
    if (out.length >= limit) return out;
  }

  const order = [...edges].sort((x, y) => x.minX - y.minX);
  for (let s = 0; s < order.length; s++) {
    const e1 = order[s]!;
    for (let t = s + 1; t < order.length; t++) {
      const e2 = order[t]!;
      if (e2.minX > e1.maxX + eps) break;
      if (e2.minY > e1.maxY + eps || e2.maxY < e1.minY - eps) continue;
      const gap = Math.abs(e1.i - e2.i);
      if (gap === 1 || gap === n - 1) continue; // adjacent: handled above
      const q = segmentIntersection(e1.a, e1.b, e2.a, e2.b, eps);
      if (q) {
        push(e1.i, e2.i, q);
        if (out.length >= limit) return out;
      }
    }
  }
  return out;
}

/** Drop consecutive duplicate corners (and a repeated closing corner). */
export function dedupeRing(ring: readonly LngLat[], toleranceM = 0.01): LngLat[] {
  const out: LngLat[] = [];
  for (const p of ring) {
    const last = out[out.length - 1];
    if (last && haversineM(last, p) <= toleranceM) continue;
    out.push([p[0], p[1]]);
  }
  while (out.length > 1 && haversineM(out[0]!, out[out.length - 1]!) <= toleranceM) out.pop();
  return out;
}

// ---------- validation ----------

export type ZoneProblemCode =
  | 'too-few-corners'
  | 'too-many-corners'
  | 'crosses-itself'
  | 'no-area'
  | 'radius-too-small'
  | 'radius-too-large'
  | 'bad-coordinates';

export interface ZoneProblem {
  code: ZoneProblemCode;
  message: string;
  /** Where to point at on the map (self-intersections). */
  points?: LngLat[];
}

export type ZoneWarningCode = 'smaller-than-gps' | 'overlaps';

export interface ZoneWarning {
  code: ZoneWarningCode;
  message: string;
}

/** Everything that makes a shape unsaveable (empty = OK). */
export function shapeProblems(shape: ZoneShape): ZoneProblem[] {
  if (shape.kind === 'circle') {
    const out: ZoneProblem[] = [];
    if (!isFiniteLngLat(shape.center)) {
      out.push({
        code: 'bad-coordinates',
        message: 'The circle centre is not a valid map position.',
      });
    }
    if (!Number.isFinite(shape.radiusM) || shape.radiusM < MIN_RADIUS_M) {
      out.push({
        code: 'radius-too-small',
        message: `The radius must be at least ${MIN_RADIUS_M} m.`,
      });
    } else if (shape.radiusM > MAX_RADIUS_M) {
      out.push({
        code: 'radius-too-large',
        message: `The radius can be at most ${fmt(MAX_RADIUS_M / 1000, 0)} km. Draw a polygon for a larger area.`,
      });
    }
    return out;
  }
  const ring = shape.ring;
  if (!ring.every(isFiniteLngLat)) {
    return [{ code: 'bad-coordinates', message: 'Some corners are not valid map positions.' }];
  }
  const distinct = dedupeRing(ring);
  if (distinct.length < MIN_VERTICES) {
    return [{ code: 'too-few-corners', message: 'A zone needs at least 3 corners.' }];
  }
  if (ring.length > MAX_VERTICES) {
    return [
      {
        code: 'too-many-corners',
        message: `A zone can have at most ${fmt(MAX_VERTICES, 0)} corners (this one has ${fmt(ring.length, 0)}). Simplify it first.`,
      },
    ];
  }
  const out: ZoneProblem[] = [];
  const crossings = findSelfIntersections(distinct);
  if (crossings.length) {
    out.push({
      code: 'crosses-itself',
      message:
        crossings.length === 1
          ? 'The outline crosses itself (red mark). Drag a corner to untangle it.'
          : `The outline crosses itself in ${crossings.length} places (red marks). Drag corners to untangle it.`,
      points: crossings.map((c) => c.point),
    });
  }
  if (!crossings.length && ringAreaM2(distinct) < MIN_AREA_M2) {
    out.push({ code: 'no-area', message: 'The corners lie on one line, so the zone has no area.' });
  }
  return out;
}

/** Things worth knowing that do not block a save. */
export function shapeWarnings(shape: ZoneShape): ZoneWarning[] {
  const small =
    shape.kind === 'circle'
      ? shape.radiusM > 0 && shape.radiusM < GPS_ACCURACY_M
      : ringAreaM2(shape.ring) > 0 && ringAreaM2(shape.ring) < GPS_ACCURACY_M * GPS_ACCURACY_M * 2;
  return small
    ? [
        {
          code: 'smaller-than-gps',
          message:
            'This zone is smaller than typical GPS accuracy, so trucks may seem to jump in and out of it.',
        },
      ]
    : [];
}

// ---------- containment, overlap ----------

/** Ray-cast point-in-ring (lng/lat plane; boundary counts as inside). */
export function pointInRing(p: LngLat, ring: readonly LngLat[]): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = ring[j]!;
    const b = ring[i]!;
    if (a[1] > p[1] !== b[1] > p[1]) {
      const x = a[0] + ((p[1] - a[1]) * (b[0] - a[0])) / (b[1] - a[1]);
      if (p[0] < x) inside = !inside;
    }
  }
  return inside;
}

export type Bounds = [[number, number], [number, number]];

export function ringBounds(ring: readonly LngLat[]): Bounds | null {
  if (!ring.length) return null;
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  for (const [lng, lat] of ring) {
    if (lng < w) w = lng;
    if (lng > e) e = lng;
    if (lat < s) s = lat;
    if (lat > n) n = lat;
  }
  return [
    [w, s],
    [e, n],
  ];
}

/** A destination `distanceM` metres from `from` on `bearingDeg` (great circle). */
export function destination(from: LngLat, bearingDeg: number, distanceM: number): LngLat {
  const d = distanceM / EARTH_RADIUS_M;
  const b = toRad(bearingDeg);
  const lat1 = toRad(from[1]);
  const lng1 = toRad(from[0]);
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(b));
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(b) * Math.sin(d) * Math.cos(lat1),
      Math.cos(d) - Math.sin(lat1) * Math.sin(lat2)
    );
  return [toDeg(lng2), toDeg(lat2)];
}

/** A circle as an open polygon ring (geodesic, `steps` corners). */
export function circleRing(center: LngLat, radiusM: number, steps = 64): LngLat[] {
  const out: LngLat[] = [];
  for (let i = 0; i < steps; i++) out.push(destination(center, (360 * i) / steps, radiusM));
  return out;
}

/** The shape's outline as an open ring (circles approximated). */
export function shapeRing(shape: ZoneShape, steps = 64): LngLat[] {
  return shape.kind === 'circle' ? circleRing(shape.center, shape.radiusM, steps) : shape.ring;
}

export function shapeBounds(shape: ZoneShape): Bounds | null {
  if (shape.kind === 'circle') {
    const n = destination(shape.center, 0, shape.radiusM);
    const s = destination(shape.center, 180, shape.radiusM);
    const e = destination(shape.center, 90, shape.radiusM);
    const w = destination(shape.center, 270, shape.radiusM);
    return [
      [w[0], s[1]],
      [e[0], n[1]],
    ];
  }
  return ringBounds(shape.ring);
}

const boundsOverlap = (a: Bounds, b: Bounds): boolean =>
  a[0][0] <= b[1][0] && b[0][0] <= a[1][0] && a[0][1] <= b[1][1] && b[0][1] <= a[1][1];

/** Edge-to-edge contact narrower than this (metres) is touching, not overlapping. */
const OVERLAP_EPS_M = 0.05;

/** Whether segments p1-p2 and p3-p4 cross properly (touching or running along each other does not count). */
function properlyCross(p1: XY, p2: XY, p3: XY, p4: XY, eps: number): boolean {
  const d1 = cross(p3, p4, p1);
  const d2 = cross(p3, p4, p2);
  const d3 = cross(p1, p2, p3);
  const d4 = cross(p1, p2, p4);
  const t12 = eps * (Math.hypot(p2[0] - p1[0], p2[1] - p1[1]) || 1);
  const t34 = eps * (Math.hypot(p4[0] - p3[0], p4[1] - p3[1]) || 1);
  return (
    ((d1 > t34 && d2 < -t34) || (d1 < -t34 && d2 > t34)) &&
    ((d3 > t12 && d4 < -t12) || (d3 < -t12 && d4 > t12))
  );
}

/** Inside the ring and clear of its outline by more than `eps` (planar). */
function strictlyInside(q: XY, ring: readonly XY[], eps: number): boolean {
  return pointInRing(q as LngLat, ring as LngLat[]) && distanceToOutline(q, ring) > eps;
}

/**
 * Whether two zones share area. Zones that only touch — side by side with a
 * shared (snapped) edge or corner — do not overlap; that is how neighbouring
 * stockpiles are meant to be drawn.
 */
export function shapesOverlap(a: ZoneShape, b: ZoneShape): boolean {
  if (a.kind === 'circle' && b.kind === 'circle') {
    return haversineM(a.center, b.center) < a.radiusM + b.radiusM - OVERLAP_EPS_M;
  }
  const ba = shapeBounds(a);
  const bb = shapeBounds(b);
  if (!ba || !bb || !boundsOverlap(ba, bb)) return false;
  const ra = shapeRing(a);
  const rb = shapeRing(b);
  if (ra.length < 3 || rb.length < 3) return false;
  const proj = projector(ra[0]!);
  const pa = ra.map(proj.to);
  const pb = rb.map(proj.to);
  const eps = OVERLAP_EPS_M;
  for (let i = 0; i < pa.length; i++) {
    const a1 = pa[i]!;
    const a2 = pa[(i + 1) % pa.length]!;
    for (let j = 0; j < pb.length; j++) {
      if (properlyCross(a1, a2, pb[j]!, pb[(j + 1) % pb.length]!, 1e-9)) return true;
    }
  }
  // No crossing edges: either apart, touching, or one inside the other. Probe
  // each ring's corners and edge midpoints (and an inside point, for identical
  // shapes) against the other ring's interior.
  const probes = (pts: readonly XY[], ring: ZoneShape): XY[] => {
    const out: XY[] = [...pts];
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i]!;
      const q = pts[(i + 1) % pts.length]!;
      out.push([(p[0] + q[0]) / 2, (p[1] + q[1]) / 2]);
    }
    out.push(proj.to(labelPoint(ring)));
    return out;
  };
  if (probes(pa, a).some((q) => strictlyInside(q, pb, eps))) return true;
  if (probes(pb, b).some((q) => strictlyInside(q, pa, eps))) return true;
  return false;
}

// ---------- reshaping ----------

export function moveVertex(ring: readonly LngLat[], index: number, to: LngLat): LngLat[] {
  return ring.map((p, i) => (i === index ? ([to[0], to[1]] as LngLat) : p));
}

/** Insert a corner after `afterIndex` (i.e. on edge afterIndex -> afterIndex + 1). */
export function insertVertex(ring: readonly LngLat[], afterIndex: number, at: LngLat): LngLat[] {
  const out = ring.slice();
  out.splice(afterIndex + 1, 0, [at[0], at[1]]);
  return out;
}

/** Remove a corner, or null when that would leave fewer than 3. */
export function removeVertex(ring: readonly LngLat[], index: number): LngLat[] | null {
  if (ring.length <= MIN_VERTICES) return null;
  return ring.filter((_, i) => i !== index);
}

/** Midpoint of every edge (edge i: vertex i -> i + 1), for the "add a corner" handles. */
export function edgeMidpoints(ring: readonly LngLat[]): LngLat[] {
  const n = ring.length;
  if (n < 2) return [];
  const out: LngLat[] = [];
  for (let i = 0; i < n; i++) {
    const a = vertexAt(ring, i);
    const b = vertexAt(ring, i + 1);
    out.push([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]);
  }
  return out;
}

/**
 * Move a whole shape so `from` lands on `to`, keeping its true size: offsets are
 * carried in metres, not degrees, so a zone dragged north does not shrink.
 */
export function translateShape(shape: ZoneShape, from: LngLat, to: LngLat): ZoneShape {
  if (shape.kind === 'circle') {
    const src = projector(from);
    const dst = projector(to);
    return { ...shape, center: dst.from(src.to(shape.center)) };
  }
  const src = projector(from);
  const dst = projector(to);
  return { kind: 'polygon', ring: shape.ring.map((p) => dst.from(src.to(p))) };
}

/** Nudge one point by a pixel offset through the map's projection. */
export function nudgePoint(
  p: LngLat,
  dxPx: number,
  dyPx: number,
  project: (p: LngLat) => [number, number],
  unproject: (q: [number, number]) => LngLat
): LngLat {
  const [x, y] = project(p);
  return unproject([x + dxPx, y + dyPx]);
}

/** Axis-aligned rectangle (in lng/lat, so square on the map) from two opposite corners. */
export function rectangleRing(a: LngLat, b: LngLat): LngLat[] {
  return [
    [a[0], a[1]],
    [b[0], a[1]],
    [b[0], b[1]],
    [a[0], b[1]],
  ];
}

/** Area-weighted centroid of an open ring (local projection); vertex mean if flat. */
export function ringCentroid(ring: readonly LngLat[]): LngLat {
  const n = ring.length;
  if (!n) return [0, 0];
  const proj = projector(ring[0]!);
  const pts = ring.map(proj.to);
  let a2 = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < n; i++) {
    const p = pts[i]!;
    const q = pts[(i + 1) % n]!;
    const f = p[0] * q[1] - q[0] * p[1];
    a2 += f;
    cx += (p[0] + q[0]) * f;
    cy += (p[1] + q[1]) * f;
  }
  if (Math.abs(a2) < 1e-9) {
    const mx = pts.reduce((s, p) => s + p[0], 0) / n;
    const my = pts.reduce((s, p) => s + p[1], 0) / n;
    return proj.from([mx, my]);
  }
  return proj.from([cx / (3 * a2), cy / (3 * a2)]);
}

/** Distance (planar, metres) from a point to a ring's outline. */
function distanceToOutline(q: XY, pts: readonly XY[]): number {
  let best = Infinity;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len2 = dx * dx + dy * dy;
    const t = len2 ? Math.max(0, Math.min(1, ((q[0] - a[0]) * dx + (q[1] - a[1]) * dy) / len2)) : 0;
    const d = Math.hypot(q[0] - (a[0] + t * dx), q[1] - (a[1] + t * dy));
    if (d < best) best = d;
  }
  return best;
}

/**
 * A point well inside the shape for the "move" handle and labels: the centroid
 * when it is inside and clear of the edges, else the best point of a coarse grid
 * (an L- or U-shaped stockpile's centroid can sit outside it).
 */
export function labelPoint(shape: ZoneShape): LngLat {
  if (shape.kind === 'circle') return shape.center;
  const ring = shape.ring;
  if (ring.length < 3) return ring[0] ?? [0, 0];
  const c = ringCentroid(ring);
  const proj = projector(c);
  const pts = ring.map(proj.to);
  const bounds = ringBounds(pts.map((p) => [p[0], p[1]] as LngLat))!;
  const size = Math.max(bounds[1][0] - bounds[0][0], bounds[1][1] - bounds[0][1]);
  if (pointInRing(c, ring) && distanceToOutline([0, 0], pts) > size * 0.15) return c;
  let best: XY | null = null;
  let bestD = -1;
  const steps = 16;
  for (let i = 1; i < steps; i++) {
    for (let j = 1; j < steps; j++) {
      const q: XY = [
        bounds[0][0] + ((bounds[1][0] - bounds[0][0]) * i) / steps,
        bounds[0][1] + ((bounds[1][1] - bounds[0][1]) * j) / steps,
      ];
      if (!pointInRing(proj.from(q), ring)) continue;
      const d = distanceToOutline(q, pts);
      if (d > bestD) {
        bestD = d;
        best = q;
      }
    }
  }
  if (best) return proj.from(best);
  return pointInRing(c, ring) ? c : ring[0]!;
}

/** Douglas-Peucker simplification (planar metres); keeps at least 3 corners. */
export function simplifyRing(ring: readonly LngLat[], toleranceM: number): LngLat[] {
  const n = ring.length;
  if (n <= 3 || toleranceM <= 0) return ring.slice();
  const proj = projector(ring[0]!);
  const pts = ring.map(proj.to);
  // Close the ring for the recursion, split at the corner farthest from the first.
  let far = 1;
  let farD = -1;
  for (let i = 1; i < n; i++) {
    const d = Math.hypot(pts[i]![0] - pts[0]![0], pts[i]![1] - pts[0]![1]);
    if (d > farD) {
      farD = d;
      far = i;
    }
  }
  const keep = new Uint8Array(n + 1);
  keep[0] = 1;
  keep[far] = 1;
  keep[n] = 1;
  const closed = [...pts, pts[0]!];
  const stack: Array<[number, number]> = [
    [0, far],
    [far, n],
  ];
  while (stack.length) {
    const [s, e] = stack.pop()!;
    const a = closed[s]!;
    const b = closed[e]!;
    let idx = -1;
    let maxD = 0;
    for (let i = s + 1; i < e; i++) {
      const d = distanceToOutline(closed[i]!, [a, b]);
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (idx > 0 && maxD > toleranceM) {
      keep[idx] = 1;
      stack.push([s, idx], [idx, e]);
    }
  }
  const out: LngLat[] = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(ring[i]!);
  return out.length >= 3 ? out : ring.slice(0, 3);
}

/** Simplify until at most `maxCorners` remain (doubling the tolerance from 0.5 m). */
export function simplifyToMax(ring: readonly LngLat[], maxCorners: number): LngLat[] {
  let out = ring.slice();
  let tol = 0.5;
  while (out.length > maxCorners && tol < 100000) {
    out = simplifyRing(ring, tol);
    tol *= 2;
  }
  return out;
}

// ---------- snapping ----------

export interface SnapSource {
  /** Zone name, shown next to the snap ring. */
  label: string;
  /** Corners to snap to (polygons; empty for circles). */
  vertices: readonly LngLat[];
  /** The outline (open ring) to snap along. */
  outline: readonly LngLat[];
}

export interface SnapResult {
  point: LngLat;
  kind: 'vertex' | 'edge';
  label: string;
}

/**
 * Snap a cursor (screen pixels) to the nearest corner of another zone within
 * `vertexPx`, else to the nearest point on another zone's outline within
 * `edgePx`. Corners win over edges so shared corners line up exactly.
 */
export function snapPoint(
  cursorPx: [number, number],
  sources: readonly SnapSource[],
  project: (p: LngLat) => [number, number],
  unproject: (q: [number, number]) => LngLat,
  { vertexPx = 14, edgePx = 10 }: { vertexPx?: number; edgePx?: number } = {}
): SnapResult | null {
  let bestV: SnapResult | null = null;
  let bestVD = vertexPx;
  let bestE: { px: [number, number]; label: string } | null = null;
  let bestED = edgePx;
  for (const src of sources) {
    for (const v of src.vertices) {
      const q = project(v);
      const d = Math.hypot(q[0] - cursorPx[0], q[1] - cursorPx[1]);
      if (d <= bestVD) {
        bestVD = d;
        bestV = { point: [v[0], v[1]], kind: 'vertex', label: src.label };
      }
    }
    const ring = src.outline;
    for (let i = 0; i < ring.length; i++) {
      const a = project(ring[i]!);
      const b = project(ring[(i + 1) % ring.length]!);
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const len2 = dx * dx + dy * dy;
      if (!len2) continue;
      const t = Math.max(
        0,
        Math.min(1, ((cursorPx[0] - a[0]) * dx + (cursorPx[1] - a[1]) * dy) / len2)
      );
      const px: [number, number] = [a[0] + t * dx, a[1] + t * dy];
      const d = Math.hypot(px[0] - cursorPx[0], px[1] - cursorPx[1]);
      if (d <= bestED) {
        bestED = d;
        bestE = { px, label: src.label };
      }
    }
  }
  if (bestV) return bestV;
  if (bestE) return { point: unproject(bestE.px), kind: 'edge', label: bestE.label };
  return null;
}

export function roundCoord(v: number, dp = 8): number {
  const f = 10 ** dp;
  return Math.round(v * f) / f;
}

export function shapesEqual(a: ZoneShape | null, b: ZoneShape | null): boolean {
  if (a === b) return true;
  if (!a || !b || a.kind !== b.kind) return false;
  if (a.kind === 'circle' && b.kind === 'circle') {
    return samePoint(a.center, b.center) && Math.abs(a.radiusM - b.radiusM) < 1e-6;
  }
  if (a.kind === 'polygon' && b.kind === 'polygon') {
    return a.ring.length === b.ring.length && a.ring.every((p, i) => samePoint(p, b.ring[i]!));
  }
  return false;
}
